import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();
import { BACKEND_URL } from '../config';
const API = `${BACKEND_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Setup Axios Interceptor for Token
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
             // console.warn("[Auth] 401 Unauthorized received. URL:", error.config.url);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Guest Check
      if (token === 'guest-token') {
          setUser({
              user_id: 'guest',
              name: 'Guest User',
              email: 'guest@consensusbigboards.com',
              picture: null,
              role: 'guest',
              isGuest: true
          });
          setLoading(false);
          return;
      }

      // Try fetching user with cookie or token
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      // Only clear if actually unauthorized
      if (error.response?.status === 401) {
          setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (response.data.token) {
        localStorage.setItem('token', response.data.token);
    }
    setUser(response.data.user);
    return response.data;
  };

  const loginAsGuest = () => {
      // Wipe guest boards on loginAsGuest to ensure fresh session
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('guest_ranking_')) {
              keysToRemove.push(key);
          }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
      
      localStorage.setItem('token', 'guest-token');
      setUser({
          user_id: 'guest',
          name: 'Guest User',
          email: 'guest@consensusbigboards.com',
          picture: null,
          role: 'guest',
          isGuest: true
      });
  };

  const register = async (email, password, name, honeypot = null) => {
    const payload = { email, password, name };
    if (honeypot) payload.honeypot = honeypot;
    const response = await axios.post(`${API}/auth/register`, payload);
    if (response.data.token === 'fake_token') {
        return response.data;
    }
    if (response.data.token) {
        localStorage.setItem('token', response.data.token);
    }
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    if (user?.isGuest) {
        localStorage.removeItem('token');
        setUser(null);
        return;
    }
    
    // Clear guest storage on logout using keys array to avoid mutation issues
    const keysToRemove = [];
    // Iterate over all keys
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('guest_ranking_')) {
            keysToRemove.push(key);
        }
    }
    // Remove safely
    keysToRemove.forEach(k => localStorage.removeItem(k));
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    setUser(null);
  };

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    // New Direct Google Auth Flow
    console.log(`[Auth] Verifying Google Token at ${API}/auth/google`);
    try {
        const response = await axios.post(`${API}/auth/google`, 
          { token: credentialResponse.credential },
          { withCredentials: true }
        );
        
        if (response.data.token) {
            localStorage.setItem('token', response.data.token);
        }
        setUser(response.data.user);
        
        if (response.data.needs_username_setup) {
            return { ...response.data, needs_username_setup: true };
        }
        return response.data;
    } catch (error) {
        // console.error("[Auth] Google Verify Error:", error.response?.data || error.message);
        throw error;
    }
  };
  
  // Kept for backward compatibility if needed, but not used in new flow
  const handleGoogleSession = async (sessionId) => {
      // Legacy flow
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginAsGuest, register, logout, handleGoogleLoginSuccess, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
